import * as React from 'react';
import { toastr } from 'react-redux-toastr';
import { useDataLoader, usePerformAction } from 'react-remote-data-hooks';
import api from '../../../../api';
import CreateForm from './CreateForm';
import {
  AppPermissions,
  useHasPermission,
  VisibilityGroup,
  CreateVisibilityGroupFormData,
} from '../../../../core';

const VisibilityGroupPage: React.FunctionComponent = () => {
  const canCreateVisiblityGroup = useHasPermission(
    AppPermissions.CreateVisiblityGroup
  );

  const [editModal, setEditModal] = React.useState<{
    data?: VisibilityGroup;
    isOpen?: boolean;
  }>({});

  const [error, setError] = React.useState<any>();

  const { data: visibilityGroups, reload } = useDataLoader<VisibilityGroup[]>(
    () => api.visibilityGroup.loadVisibilityGroup()
  );

  const {
    performAction: createVisibilityGroup,
    isPerforming: isCreating,
  } = usePerformAction(
    (q: CreateVisibilityGroupFormData) => {
      setError('');
      return api.visibilityGroup.createVisibilityGroup(q);
    },
    () => {
      setEditModal({});
      reload();
    },
    setError
  );

  const {
    performAction: updateVisibilityGroup,
    isPerforming: isUpdating,
  } = usePerformAction(
    (id, q: CreateVisibilityGroupFormData) => {
      setError('');
      return api.visibilityGroup.updateVisibilityGroup(q, id);
    },
    () => {
      setEditModal({});
      reload();
    },
    setError
  );

  const update = (group: CreateVisibilityGroupFormData) => {
    const _id = editModal?.data?._id;
    if (!_id) {
      createVisibilityGroup(group);
    } else {
      updateVisibilityGroup(_id, group);
    }
  };

  const onDelete = async (id?: string) => {
    try {
      await api.visibilityGroup.deleteVisibilityGroup(id);
      toastr.success('Success', 'Visibility Group deleted successfully');
      setEditModal({});
      reload();
    } catch (error) {
      if (error?.response?.data?.message) {
        toastr.error('Error', error?.response?.data?.message);
      }
    }
  };

  const closeModal = () => {
    setEditModal({});
  };

  const renderTableRow = (rowData: any[], level: number): any => {
    return (
      <>
        {rowData?.map((group, index) => {
          const childRow = visibilityGroups?.filter(
            (x) => x.parentId?.toString() === group._id?.toString()
          );

          return (
            <>
              <tr
                key={index}
                onClick={() => {
                  if (canCreateVisiblityGroup) {
                    setError('');
                    setEditModal({
                      isOpen: true,
                      data: group,
                    });
                  }
                }}
                className="cursor-pointer"
              >
                <td className="font-normal py-3">
                  {!!group?.parents?.length && (
                    <i
                      style={{
                        paddingLeft: level * 15,
                      }}
                      className="icon-bottom-right pr-2 font-base"
                    ></i>
                  )}

                  {group.title}
                </td>
              </tr>
              {!!childRow?.length && renderTableRow(childRow, level + 1)}
            </>
          );
        })}
      </>
    );
  };

  const topLevelVisibilityGroup = visibilityGroups?.filter((v) => !v.parentId);

  return (
    <>
      <div className="col-lg-5 col-md-6 col-12 border-right">
        <div className="d-flex align-items-center py-3">
          <div className="col-auto">
            <h4 className="mb-0 font-weight-semibold">Visibility Group</h4>
          </div>
          {/* <div className="col d-flex role-view-types">
            <div className="d-flex align-items-center justify-content-center rounded-sm cursor-pointer mr-2">
              <i className="icon-list-view"></i>
            </div>
            <div className="d-flex align-items-center justify-content-center rounded-sm cursor-pointer active">
              <i className="icon-ladder-view"></i>
            </div>
          </div> */}
          {canCreateVisiblityGroup && (
            <div className="col-auto text-right">
              <button
                className="btn btn-link font-normal font-weight-semibold px-2 py-2 d-inline-flex align-items-center"
                onClick={() => {
                  setError('');
                  setEditModal({ isOpen: true });
                }}
              >
                <i className="icon-plus mr-1 font-xs"></i> Add
              </button>
            </div>
          )}
        </div>
        <div className="grid-table grid-table--sm border-top">
          <table>
            <thead>
              <tr>
                <th>
                  <span className="d-inline-flex align-items-center">Name</span>
                </th>
              </tr>
            </thead>
            <tbody>{renderTableRow(topLevelVisibilityGroup, 0)}</tbody>
          </table>
        </div>
      </div>
      <div className="col-lg col-md-6 col-12 p-2 setting-detail-block left-0 top-0 bg-white w-100 h-100 zIndex-1">
        {editModal.isOpen && (
          <>
            <div className="text-right d-md-none">
              <i className="icon-close"></i>
            </div>
            <CreateForm
              key={`${editModal?.data?._id || 'create'}`}
              onSubmit={update}
              isSubmitting={isUpdating || isCreating}
              error={
                error
                  ? error.response?.message ||
                    `Unable to ${editModal.data ? 'update' : 'create'}`
                  : undefined
              }
              data={editModal.data}
              onCancel={() => closeModal()}
              onDelete={(id?: string) => onDelete(id)}
              groups={visibilityGroups}
            ></CreateForm>
          </>
        )}
      </div>
    </>
  );
};

export default VisibilityGroupPage;
